import "@diligentcorp/atlas-web-component-icons/lib/keyboard/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/close/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/arrow-up/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/arrow-down/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/arrow-left/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/arrow-right/index.js";
import "./PlaceholderImage.js";
import { msg, updateWhenLocaleChanges } from "@lit/localize";
import { css, html, LitElement } from "lit";
import { map } from "lit/directives/map.js";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";
import { commandLabels, DELIMITERS, macShortcutKeys, sectionLabels, sectionOrder, shortcutKeyLabels, winShortcutKeys, } from "../configuration/shortcuts.js";
import { defineComponent, dispatchToggleModalEvent, isMac, isRestrictedEnv, } from "../configuration/utils.js";
import { buttonDefaults, coreVar, iconButton, mediumButton, secondaryButton, semanticVar, tertiaryButton, } from "./styles.js";
const SHORTCUTS = isMac ? macShortcutKeys : winShortcutKeys;
const BODY_SHORTCUTS_OPEN_CLASS = "atlas-gn-shortcuts--open";
/**
 * The `atlas-gn-shortcuts-panel` component is a panel that displays a list of keyboard shortcuts.
 *
 * @tag atlas-gn-shortcuts-panel
 * @prop {boolean} open - Whether the panel is open.
 * @prop {ShortcutAppKey} section - The section of shortcuts to display.
 * @prop {boolean} usePendo - Whether to use Pendo for the shortcuts panel (to request shortcuts).
 * @fires {CustomEvent<{ open: boolean }>} atlas-gn:toggle-shortcuts
 */
export class ShortcutsPanel extends LitElement {
    constructor() {
        super();
        this.dialogRef = createRef();
        this.handleGlobalKeydown = (event) => {
            if (event.key === "/" && (event.metaKey || event.ctrlKey)) {
                this.togglePanel();
            }
            if (this.open && event.key === "Escape") {
                this.togglePanel();
            }
            event.stopPropagation();
        };
        this.renderSection = (section) => {
            if (section === "bubbly") {
                return this.renderPlaceholder();
            }
            if (section in SHORTCUTS) {
                const label = sectionLabels[section]();
                const items = SHORTCUTS[section] || {};
                const entries = Object.entries(items);
                return html `
        <section>
          <h3>${label}</h3>
          ${map(entries, ([name, shortcut]) => html `
              <dl>
                <dt>${commandLabels[name]()}</dt>
                <dd>${map(shortcut, this.renderKeyValue)}</dd>
              </dl>
            `)}
        </section>
      `;
            }
        };
        this.renderKeyValue = (key) => {
            if (key.length === 1)
                return html `<kbd>${key.toUpperCase()}</kbd>`;
            switch (key) {
                case "metaKey":
                    return html `<kbd>⌘</kbd>`;
                case "arrowup":
                    return html `<dil-arrow-up-icon
          size="md"
          class="icon"
          aria-label=${shortcutKeyLabels.arrowup}
        ></dil-arrow-up-icon>`;
                case "arrowdown":
                    return html `<dil-arrow-down-icon
          size="md"
          class="icon"
          aria-label=${shortcutKeyLabels.arrowdown}
        ></dil-arrow-down-icon>`;
                case "arrowleft":
                    return html `<dil-arrow-left-icon
          size="md"
          class="icon"
          aria-label=${shortcutKeyLabels.arrowleft}
        ></dil-arrow-left-icon>`;
                case "arrowright":
                    return html `<dil-arrow-right-icon
          size="md"
          class="icon"
          aria-label=${shortcutKeyLabels.arrowright}
        ></dil-arrow-right-icon>`;
                case DELIMITERS.COMMA:
                    return html `<span> , </span>`;
                case DELIMITERS.BACKSLASH:
                    return html `<span> / </span>`;
                case DELIMITERS.PLUS:
                    return html `<span> + </span>`;
                default:
                    return html `<kbd>${shortcutKeyLabels[key]()}</kbd>`;
            }
        };
        this.togglePanel = () => {
            this.open = !this.open;
            if (this.open) {
                this.dialogRef.value?.showModal();
                document.body.classList.add(BODY_SHORTCUTS_OPEN_CLASS);
            }
            else {
                this.dialogRef.value?.close();
                document.body.classList.remove(BODY_SHORTCUTS_OPEN_CLASS);
            }
            this.dispatchToggleEvent();
        };
        this.dispatchToggleEvent = () => {
            dispatchToggleModalEvent(this, this.open, "panel");
        };
        this.handleDialogClick = (event) => {
            if (event.target === this.dialogRef.value) {
                this.togglePanel();
            }
        };
        this.open = false;
        this.section = "default";
        this.usePendo = false;
        updateWhenLocaleChanges(this);
    }
    connectedCallback() {
        super.connectedCallback();
        this.addGlobalKeydownListener();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeGlobalKeydownListener();
    }
    addGlobalKeydownListener() {
        document.addEventListener("keydown", this.handleGlobalKeydown);
    }
    removeGlobalKeydownListener() {
        document.removeEventListener("keydown", this.handleGlobalKeydown);
    }
    get currentSectionKeys() {
        if (Object.keys(sectionOrder).includes(this.section)) {
            return sectionOrder[this.section];
        }
        else {
            return sectionOrder["default"];
        }
    }
    render() {
        const shortcutsShortcut = `${isMac ? "⌘" : "Ctrl"} + /`;
        return html `
      <button
        class="tertiary medium icon-button"
        @click=${this.togglePanel}
        aria-haspopup=${"dialog"}
        aria-expanded=${this.open ? "true" : "false"}
        aria-controls=${"atlas-gn-shortcuts-panel"}
        aria-label=${msg(`Keyboard shortcuts`)}
        title=${`${msg(`Open keyboard shortcuts dialog`)} (${shortcutsShortcut})`}
      >
        <dil-keyboard-icon aria-hidden="true"></dil-keyboard-icon>
      </button>
      <dialog
        ${ref(this.dialogRef)}
        id=${"atlas-gn-shortcuts-panel"}
        aria-label=${msg(`Keyboard shortcuts`)}
        aria-hidden=${this.open ? "false" : "true"}
        @click=${this.handleDialogClick}
        @close=${() => {
            if (this.open) {
                this.open = false;
            }
            this.dispatchToggleEvent();
        }}
      >
        ${when(this.open, () => html `
            <header>
              <h2 id="atlas-gn-shortcuts-panel-title">${msg(`Keyboard shortcuts`)}</h2>
              <button
                class="tertiary medium icon-button"
                @click=${this.togglePanel}
                aria-label=${msg(`Close shortcuts dialog`)}
                aria-controls="atlas-gn-shortcuts-panel"
                tabindex="0"
              >
                <dil-close-icon aria-hidden="true"></dil-close-icon>
              </button>
            </header>
            <main>${map(this.currentSectionKeys, this.renderSection)}</main>
          `)}
      </dialog>
    `;
    }
    renderPlaceholder() {
        const usePendo = !isRestrictedEnv && this.usePendo;
        return html `
      <section class="placeholder">
        <atlas-gn-placeholder-image> </atlas-gn-placeholder-image>
        <h3>${msg(`No app-specific shortcuts available.`)}</h3>
        <p>
          ${when(usePendo, () => msg(`We don't support specific shortcuts for this app but if you have any suggestions, please let us know.`), () => msg(`We don't support specific shortcuts for this app.`))}
        </p>
        ${when(usePendo, () => html `<button class="secondary medium" data-pendo-anchor="request-shortcuts">
              ${msg(`Request keyboard shortcuts`)}
            </button>`)}
      </section>
    `;
    }
}
ShortcutsPanel.properties = {
    open: { type: Boolean, reflect: true },
    section: { type: String, reflect: true },
    usePendo: { type: Boolean, reflect: true, attribute: "use-pendo" },
};
ShortcutsPanel.styles = [
    buttonDefaults,
    secondaryButton,
    tertiaryButton,
    mediumButton,
    iconButton,
    css `
      :host {
        display: flex;
      }

      dialog {
        border: none;
        padding: 0;
        margin-right: -300px;
        margin-bottom: 0;
        margin-top: 0;
        height: 100svh;
        max-height: 100vh;
        width: 33%;
        min-width: 300px;
        flex-direction: column;
        animation: slide-in var(--atlas-global-nav-animation-duration)
          cubic-bezier(0.33, 1, 0.68, 1);
        box-shadow: var(--lens-component-modal-box-shadow);
        z-index: calc(Infinity);
      }
      dialog[open] {
        display: flex;
        margin-right: 0;
      }
      dialog::backdrop {
        background: #282e3780;
      }

      dialog header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: ${coreVar("spacing-3")};
        border-bottom-width: ${semanticVar("border-width-thin")};
        border-bottom-color: ${semanticVar("color-ui-divider-default")};
        border-bottom-style: solid;
      }
      dialog header h2 {
        font-size: ${semanticVar("font-title-h4-md-font-size")};
        line-height: ${semanticVar("font-title-h4-md-line-height")};
        font-weight: ${semanticVar("font-weight-emphasis")};
        margin: 0;
      }

      dialog main {
        overflow-y: auto;
        flex-grow: 1;
        padding: ${coreVar("spacing-3")};
      }

      @media (max-width: 1279px) {
        :host {
          display: none;
        }
      }

      dl {
        display: grid;
        grid-template-columns: 1fr auto;
        grid-template-rows: auto;
        margin: 0;
        align-items: center;
      }

      dd {
        display: inline-flex;
        align-items: center;
        gap: ${coreVar("spacing-0-5")};
        font-size: ${semanticVar("font-text-md-font-size")};
        line-height: ${semanticVar("font-text-md-line-height")};
      }

      dd span {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: ${coreVar("spacing-0-5")};
      }

      main {
        display: flex;
        flex-direction: column;
        gap: ${coreVar("spacing-3")};
      }

      section {
        display: flex;
        flex-direction: column;
        gap: ${coreVar("spacing-2")};
      }
      section h3 {
        padding: 0;
        margin: 0;
        padding-bottom: ${coreVar("spacing-2")};
        font-size: ${semanticVar("font-title-h5-sm-font-size")};
        line-height: ${semanticVar("font-title-h5-sm-line-height")};
        font-weight: ${semanticVar("font-weight-emphasis")};
        border-bottom-width: ${semanticVar("border-width-thin")};
        border-bottom-color: ${semanticVar("color-ui-divider-default")};
        border-bottom-style: solid;
      }
      section + section {
        padding-top: ${coreVar("spacing-3")};
      }

      kbd,
      .icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: ${coreVar("spacing-0-5")};
        border-radius: ${semanticVar("radius-sm")};
        border-width: ${semanticVar("border-width-thin")};
        border-color: ${semanticVar("color-ui-divider-default")};
        border-style: solid;
        background: ${coreVar("color-gray-100")};
        box-shadow: ${semanticVar("box-shadow-1")};
        font: inherit;
        min-width: 1.2em;
      }

      .placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: ${coreVar("spacing-2")};
        margin-top: ${coreVar("spacing-4")};
      }

      .placeholder h3 {
        border-bottom: none;
      }

      .placeholder h3,
      .placeholder p {
        padding-top: ${coreVar("spacing-0-5")};
        padding-bottom: ${coreVar("spacing-0-5")};
        margin: 0;
      }

      .placeholder button {
        margin-top: ${coreVar("spacing-2")};
      }

      @keyframes slide-in {
        from {
          margin-right: -300px;
        }
        to {
          margin-right: 0;
        }
      }
    `,
];
export const SHORTCUTS_PANEL_TAG = "atlas-gn-shortcuts-panel";
defineComponent(SHORTCUTS_PANEL_TAG, ShortcutsPanel);
